import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Textarea,
  Typography,
} from '@mui/joy';
import { FaPen, FaTrash } from 'react-icons/fa';
import { IconWrapper } from '../../../components/IconWrapper';
import { formatGbp } from '../../accounts/arrangements.model';
import {
  cashBalances,
  signedAmount,
  toIsoDate,
  withRunningBalance,
  type CashEntry,
  type CashSource,
} from '../ledger.model';
import { ledgerService } from '../ledger.service';

const amountCell = { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const };

/** Cash held outside the bank: one running ledger per holding, money in and out. */
export const CashLedgerTab: React.FC = () => {
  const [sources, setSources] = useState<CashSource[]>([]);
  const [entries, setEntries] = useState<CashEntry[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sourceModal, setSourceModal] = useState<CashSource | 'new' | null>(null);
  const [editingEntry, setEditingEntry] = useState<CashEntry | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sourceRows, entryRows] = await Promise.all([ledgerService.listSources(), ledgerService.listEntries()]);
      setSources(sourceRows);
      setEntries(entryRows);
      setSelectedId((current) => current ?? sourceRows.find((row) => !row.isArchived)?.id ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cash ledger');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const balances = useMemo(() => cashBalances(entries), [entries]);
  const visibleSources = sources.filter((source) => showArchived || !source.isArchived || source.id === selectedId);
  const activeSources = sources.filter((source) => !source.isArchived);
  const totalHeld = activeSources.reduce((sum, source) => sum + (balances.get(source.id) ?? 0), 0);
  const selected = sources.find((source) => source.id === selectedId) ?? null;
  const ledger = useMemo(
    () => withRunningBalance(entries.filter((entry) => entry.sourceId === selectedId)).reverse(),
    [entries, selectedId],
  );
  const sourceName = (id: string) => sources.find((source) => source.id === id)?.name ?? 'another holding';

  const counterpart = (entry: CashEntry) =>
    entry.transferId ? entries.find((other) => other.transferId === entry.transferId && other.id !== entry.id) : undefined;

  const removeEntry = async (entry: CashEntry) => {
    const message = entry.transferId
      ? 'Delete this transfer? Both sides of it will be removed.'
      : `Delete "${entry.description}"?`;
    if (!window.confirm(message)) return;
    try {
      await ledgerService.deleteEntry(entry);
      setEntries((current) => current.filter((row) => (entry.transferId ? row.transferId !== entry.transferId : row.id !== entry.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete entry');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Stack spacing={2}>
      {error && <Alert color="danger">{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
        <Typography level="body-sm" textColor="neutral.600">
          Total cash held: <strong>{formatGbp(totalHeld)}</strong> across {activeSources.length} holding{activeSources.length === 1 ? '' : 's'}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {sources.some((source) => source.isArchived) && (
            <Checkbox size="sm" label="Show archived" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} />
          )}
          <Button size="sm" variant="outlined" color="neutral" disabled={activeSources.length < 2} onClick={() => setTransferOpen(true)}>
            Transfer
          </Button>
          <Button size="sm" variant="outlined" onClick={() => setSourceModal('new')}>New holding</Button>
        </Stack>
      </Stack>

      {sources.length === 0 ? (
        <Sheet variant="soft" sx={{ p: 3, borderRadius: 'sm', textAlign: 'center' }}>
          <Typography level="body-sm" sx={{ mb: 1.5 }}>No cash holdings yet. Add one for each place cash is kept, e.g. the office safe.</Typography>
          <Button size="sm" onClick={() => setSourceModal('new')}>Add a cash holding</Button>
        </Sheet>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
          {visibleSources.map((source) => {
            const balance = balances.get(source.id) ?? 0;
            const isSelected = source.id === selectedId;
            return (
              <Card
                key={source.id}
                variant={isSelected ? 'soft' : 'outlined'}
                color={isSelected ? 'primary' : 'neutral'}
                onClick={() => setSelectedId(source.id)}
                sx={{ cursor: 'pointer', gap: 0.5 }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography level="title-sm" noWrap>{source.name}</Typography>
                  {source.isArchived && <Chip size="sm" variant="outlined">Archived</Chip>}
                </Stack>
                <Typography level="h4" color={balance < 0 ? 'danger' : undefined} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatGbp(balance)}
                </Typography>
                {source.description && <Typography level="body-xs" textColor="neutral.500" noWrap>{source.description}</Typography>}
              </Card>
            );
          })}
        </Box>
      )}

      {selected && (
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography level="title-md">{selected.name}</Typography>
            <Button size="sm" variant="plain" color="neutral" onClick={() => setSourceModal(selected)}>Edit holding</Button>
          </Stack>

          {!selected.isArchived && (
            <AddEntryRow sourceId={selected.id} onAdded={(entry) => setEntries((current) => [...current, entry])} />
          )}

          <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
            <Table hoverRow sx={{ minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Date</th>
                  <th>Description</th>
                  <th style={{ ...amountCell, width: 120 }}>In</th>
                  <th style={{ ...amountCell, width: 120 }}>Out</th>
                  <th style={{ ...amountCell, width: 130 }}>Balance</th>
                  <th style={{ width: 84 }} />
                </tr>
              </thead>
              <tbody>
                {ledger.map((entry) => {
                  const other = counterpart(entry);
                  return (
                    <tr key={entry.id}>
                      <td>{entry.entryDate}</td>
                      <td>
                        {entry.description}
                        {entry.transferId && (
                          <Typography level="body-xs" textColor="neutral.500" display="block">
                            Transfer {entry.amount < 0 ? 'to' : 'from'} {other ? sourceName(other.sourceId) : 'another holding'}
                          </Typography>
                        )}
                      </td>
                      <td style={amountCell}>{entry.amount > 0 ? formatGbp(entry.amount) : ''}</td>
                      <td style={amountCell}>{entry.amount < 0 ? formatGbp(-entry.amount) : ''}</td>
                      <td style={{ ...amountCell, fontWeight: 600, color: entry.balance < 0 ? 'var(--joy-palette-danger-600)' : undefined }}>
                        {formatGbp(entry.balance)}
                      </td>
                      <td>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {!entry.transferId && (
                            <IconButton size="sm" variant="plain" title="Edit" onClick={() => setEditingEntry(entry)}>
                              <IconWrapper icon={FaPen} style={{ fontSize: 12, display: 'inline-flex' }} />
                            </IconButton>
                          )}
                          <IconButton size="sm" variant="plain" color="danger" title="Delete" onClick={() => void removeEntry(entry)}>
                            <IconWrapper icon={FaTrash} style={{ fontSize: 12, display: 'inline-flex' }} />
                          </IconButton>
                        </Stack>
                      </td>
                    </tr>
                  );
                })}
                {ledger.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <Typography level="body-sm" textColor="neutral.500" sx={{ py: 2, textAlign: 'center' }}>
                        No entries yet. Record the opening balance as cash in.
                      </Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Sheet>
        </Stack>
      )}

      <SourceModal
        source={sourceModal}
        onClose={() => setSourceModal(null)}
        onSaved={(source) => {
          setSourceModal(null);
          setSources((current) => (current.some((row) => row.id === source.id)
            ? current.map((row) => (row.id === source.id ? source : row))
            : [...current, source]));
          setSelectedId(source.id);
        }}
      />
      <EditEntryModal
        entry={editingEntry}
        onClose={() => setEditingEntry(null)}
        onSaved={(entry) => {
          setEditingEntry(null);
          setEntries((current) => current.map((row) => (row.id === entry.id ? entry : row)));
        }}
      />
      <TransferModal
        open={transferOpen}
        sources={activeSources}
        defaultFromId={selected && !selected.isArchived ? selected.id : null}
        onClose={() => setTransferOpen(false)}
        onSaved={(rows) => {
          setTransferOpen(false);
          setEntries((current) => [...current, ...rows]);
        }}
      />
    </Stack>
  );
};

const AddEntryRow: React.FC<{ sourceId: string; onAdded: (entry: CashEntry) => void }> = ({ sourceId, onAdded }) => {
  const [entryDate, setEntryDate] = useState(() => toIsoDate(new Date()));
  const [description, setDescription] = useState('');
  const [cashIn, setCashIn] = useState('');
  const [cashOut, setCashOut] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const amount = signedAmount(cashIn, cashOut);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!description.trim() || amount == null) return;
    setSaving(true);
    setError(null);
    try {
      onAdded(await ledgerService.saveEntry({ sourceId, entryDate, description, amount }));
      setDescription('');
      setCashIn('');
      setCashOut('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-end' }}>
        <FormControl size="sm" sx={{ width: { md: 150 } }}>
          <FormLabel>Date</FormLabel>
          <Input type="date" value={entryDate} onChange={(event) => setEntryDate(event.target.value)} required />
        </FormControl>
        <FormControl size="sm" sx={{ flex: 1 }}>
          <FormLabel>Description</FormLabel>
          <Input value={description} placeholder="e.g. Cash from customer, paid labourer" onChange={(event) => setDescription(event.target.value)} />
        </FormControl>
        <FormControl size="sm" sx={{ width: { md: 120 } }}>
          <FormLabel>In £</FormLabel>
          <Input type="number" inputMode="decimal" slotProps={{ input: { min: 0, step: '0.01' } }} value={cashIn} onChange={(event) => { setCashIn(event.target.value); if (event.target.value) setCashOut(''); }} />
        </FormControl>
        <FormControl size="sm" sx={{ width: { md: 120 } }}>
          <FormLabel>Out £</FormLabel>
          <Input type="number" inputMode="decimal" slotProps={{ input: { min: 0, step: '0.01' } }} value={cashOut} onChange={(event) => { setCashOut(event.target.value); if (event.target.value) setCashIn(''); }} />
        </FormControl>
        <Button size="sm" type="submit" loading={saving} disabled={!description.trim() || amount == null}>Add</Button>
      </Stack>
      {error && <Alert size="sm" color="danger" sx={{ mt: 1 }}>{error}</Alert>}
    </form>
  );
};

const SourceModal: React.FC<{
  source: CashSource | 'new' | null;
  onClose: () => void;
  onSaved: (source: CashSource) => void;
}> = ({ source, onClose, onSaved }) => {
  const existing = source && source !== 'new' ? source : null;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isArchived, setIsArchived] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(existing?.name ?? '');
    setDescription(existing?.description ?? '');
    setIsArchived(existing?.isArchived ?? false);
    setError(null);
  }, [source]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      onSaved(await ledgerService.saveSource({ id: existing?.id, name, description, isArchived }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={source != null} onClose={() => !saving && onClose()}>
      <ModalDialog sx={{ width: { xs: '100%', sm: 440 } }}>
        <Typography level="title-lg">{existing ? 'Edit cash holding' : 'New cash holding'}</Typography>
        <Stack spacing={1.5}>
          <FormControl required>
            <FormLabel>Name</FormLabel>
            <Input autoFocus value={name} placeholder="e.g. Office safe" onChange={(event) => setName(event.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel>Notes</FormLabel>
            <Textarea minRows={2} value={description} onChange={(event) => setDescription(event.target.value)} />
          </FormControl>
          {existing && (
            <Checkbox label="Archived (no longer used)" checked={isArchived} onChange={(event) => setIsArchived(event.target.checked)} />
          )}
          {error && <Alert color="danger">{error}</Alert>}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="plain" color="neutral" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={() => void save()} loading={saving} disabled={!name.trim()}>Save</Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

const EditEntryModal: React.FC<{
  entry: CashEntry | null;
  onClose: () => void;
  onSaved: (entry: CashEntry) => void;
}> = ({ entry, onClose, onSaved }) => {
  const [entryDate, setEntryDate] = useState('');
  const [description, setDescription] = useState('');
  const [cashIn, setCashIn] = useState('');
  const [cashOut, setCashOut] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const amount = signedAmount(cashIn, cashOut);

  useEffect(() => {
    if (!entry) return;
    setEntryDate(entry.entryDate);
    setDescription(entry.description);
    setCashIn(entry.amount > 0 ? String(entry.amount) : '');
    setCashOut(entry.amount < 0 ? String(-entry.amount) : '');
    setError(null);
  }, [entry]);

  const save = async () => {
    if (!entry || amount == null) return;
    setSaving(true);
    setError(null);
    try {
      onSaved(await ledgerService.saveEntry({ id: entry.id, sourceId: entry.sourceId, entryDate, description, amount }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={entry != null} onClose={() => !saving && onClose()}>
      <ModalDialog sx={{ width: { xs: '100%', sm: 480 } }}>
        <Typography level="title-lg">Edit entry</Typography>
        <Stack spacing={1.5}>
          <FormControl>
            <FormLabel>Date</FormLabel>
            <Input type="date" value={entryDate} onChange={(event) => setEntryDate(event.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel>Description</FormLabel>
            <Input value={description} onChange={(event) => setDescription(event.target.value)} />
          </FormControl>
          <Stack direction="row" spacing={1}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>In £</FormLabel>
              <Input type="number" value={cashIn} onChange={(event) => { setCashIn(event.target.value); if (event.target.value) setCashOut(''); }} />
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Out £</FormLabel>
              <Input type="number" value={cashOut} onChange={(event) => { setCashOut(event.target.value); if (event.target.value) setCashIn(''); }} />
            </FormControl>
          </Stack>
          {error && <Alert color="danger">{error}</Alert>}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="plain" color="neutral" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={() => void save()} loading={saving} disabled={!description.trim() || amount == null}>Save</Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

const TransferModal: React.FC<{
  open: boolean;
  sources: CashSource[];
  defaultFromId: string | null;
  onClose: () => void;
  onSaved: (entries: CashEntry[]) => void;
}> = ({ open, sources, defaultFromId, onClose, onSaved }) => {
  const [fromId, setFromId] = useState<string | null>(null);
  const [toId, setToId] = useState<string | null>(null);
  const [entryDate, setEntryDate] = useState('');
  const [amountText, setAmountText] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const amount = signedAmount(amountText, '');

  useEffect(() => {
    if (!open) return;
    setFromId(defaultFromId);
    setToId(null);
    setEntryDate(toIsoDate(new Date()));
    setAmountText('');
    setDescription('');
    setError(null);
  }, [open, defaultFromId]);

  const save = async () => {
    if (!fromId || !toId || amount == null) return;
    setSaving(true);
    setError(null);
    try {
      onSaved(await ledgerService.transfer({ fromId, toId, entryDate, amount, description }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record transfer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !saving && onClose()}>
      <ModalDialog sx={{ width: { xs: '100%', sm: 480 } }}>
        <Typography level="title-lg">Transfer cash</Typography>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>From</FormLabel>
              <Select value={fromId} onChange={(_, value) => setFromId(value)}>
                {sources.map((source) => <Option key={source.id} value={source.id}>{source.name}</Option>)}
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>To</FormLabel>
              <Select value={toId} onChange={(_, value) => setToId(value)}>
                {sources.filter((source) => source.id !== fromId).map((source) => <Option key={source.id} value={source.id}>{source.name}</Option>)}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction="row" spacing={1}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Date</FormLabel>
              <Input type="date" value={entryDate} onChange={(event) => setEntryDate(event.target.value)} />
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Amount £</FormLabel>
              <Input type="number" value={amountText} onChange={(event) => setAmountText(event.target.value)} />
            </FormControl>
          </Stack>
          <FormControl>
            <FormLabel>Note</FormLabel>
            <Input value={description} placeholder="Transfer" onChange={(event) => setDescription(event.target.value)} />
          </FormControl>
          {error && <Alert color="danger">{error}</Alert>}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="plain" color="neutral" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={() => void save()} loading={saving} disabled={!fromId || !toId || fromId === toId || amount == null || amount <= 0}>
              Transfer
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};
